import { useState } from "react";
import { NavLink as RouterNavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Receipt,
  ClipboardCheck,
  Boxes,
  PackagePlus,
  BookOpen,
  Tag,
  Truck,
  Landmark,
  BarChart3,
  Plug,
  LifeBuoy,
  Settings,
  LogOut,
  Store as StoreIcon,
  ChevronLeft,
  Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/daily-close", label: "Daily Close", icon: ClipboardCheck },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/reorders", label: "Purchase Orders", icon: PackagePlus, end: true },
  { to: "/reorders/suggestions", label: "Reorder Suggestions", icon: PackagePlus },
  { to: "/pricebook", label: "Pricebook", icon: BookOpen },
  { to: "/promotions", label: "Promotions", icon: Tag },
  { to: "/vendors", label: "Vendors", icon: Truck },
  { to: "/banking", label: "Banking & Finance", icon: Landmark },
  { to: "/lottery", label: "Lottery", icon: Ticket },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/integrations", label: "Integration Hub", icon: Plug },
  { to: "/support", label: "Support", icon: LifeBuoy },
  { to: "/settings", label: "Settings & Audit", icon: Settings },
];

export function Sidebar({
  collapsed,
  onToggle,
  onNavigate,
  hideToggle = false,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  hideToggle?: boolean;
}) {
  const [logoutOpen, setLogoutOpen] = useState(false);
  const navigate = useNavigate();
  const { signOut, user, roles } = useAuth();

  const doSignOut = async () => {
    await signOut();
    setLogoutOpen(false);
    navigate("/auth");
  };

  return (
    <aside
      className={cn(
        "h-full md:h-screen md:sticky md:top-0 shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-200",
        collapsed ? "w-[72px]" : "w-[252px]"
      )}
    >
      <div className="flex items-center gap-2.5 px-4 h-16 border-b border-sidebar-border">
        <div className="h-9 w-9 rounded-md bg-gradient-primary flex items-center justify-center shadow-glow shrink-0">
          <StoreIcon className="h-4 w-4 text-primary-foreground" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="text-sm font-semibold text-foreground">StoreLedger</div>
            <div className="text-[11px] text-muted-foreground tracking-wider uppercase">Pro</div>
          </div>
        )}
        {!hideToggle && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onToggle}
            className={cn("ml-auto h-8 w-8 text-muted-foreground hover:text-foreground", collapsed && "rotate-180")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {items.map(({ to, label, icon: Icon, end }) => (
          <RouterNavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-primary pl-[10px]"
                  : "text-sidebar-foreground"
              )
            }
            title={collapsed ? label : undefined}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
          </RouterNavLink>
        ))}
      </nav>

      <div className="p-2 border-t border-sidebar-border">
        <button
          onClick={() => setLogoutOpen(true)}
          className={cn(
            "w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm",
            "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign out of StoreLedger Pro?</DialogTitle>
            <DialogDescription>
              {user?.email
                ? `Signed in as ${user.email}${roles.length ? ` (${roles.join(", ")})` : ""}.`
                : "You'll be returned to the sign-in screen."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutOpen(false)}>Cancel</Button>
            <Button onClick={doSignOut}>Sign out</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
